import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Handshake, Check, X, Repeat, Loader2, ChevronRight } from 'lucide-react';

const fmtTimeLeft = (iso) => {
  if (!iso) return '';
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h >= 1 ? `${h}h ${m}m left` : `${m}m left`;
};

const statusColor = {
  pending: 'text-amber-300',
  countered: 'text-orange-300',
  accepted: 'text-emerald-300',
  rejected: 'text-red-400',
  withdrawn: 'text-slate-500',
  expired: 'text-slate-500',
};

function OfferRow({ offer, role, onUpdated }) {
  const navigate = useNavigate();
  const [acting, setActing] = useState('');
  const [showCounter, setShowCounter] = useState(false);
  const [counter, setCounter] = useState('');

  const isPending = offer.status === 'pending';
  const isCountered = offer.status === 'countered';
  const showAmount = isCountered ? offer.counter_amount : offer.offer_amount;
  const counterMin = +offer.offer_amount + 0.01;
  const counterMax = +offer.bin_price - 0.01;

  const act = async (path, payload) => {
    setActing(path);
    try {
      await axios.post(`${API}/offers/${offer.offer_id}/${path}`, payload || {});
      toast.success(`Offer ${path}ed`);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${path}`);
    } finally {
      setActing('');
      setShowCounter(false);
    }
  };

  const submitCounter = () => {
    const v = parseFloat(counter);
    if (isNaN(v) || v <= +offer.offer_amount || v >= +offer.bin_price) {
      toast.error(`Counter must be between ${counterMin.toFixed(2)} and ${counterMax.toFixed(2)}`);
      return;
    }
    act('counter', { counter_amount: v });
  };

  return (
    <div
      data-testid={`offer-row-${offer.offer_id.slice(0,8)}`}
      className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">
            {role === 'inbox' ? `From @${offer.buyer_username || 'Anon'}` : `On ${offer.room_name}`}
          </p>
          <button
            onClick={() => navigate(`/game/${offer.game_id}`)}
            className="text-sm font-semibold text-white truncate hover:text-amber-300 inline-flex items-center gap-1"
          >
            {offer.room_name} <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <span className={`text-[11px] uppercase tracking-wider font-bold ${statusColor[offer.status] || 'text-slate-400'}`}>
          {offer.status}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 bg-slate-900/50 rounded-lg p-2 text-center">
        <div>
          <p className="text-[9px] text-slate-500">{isCountered ? 'Counter' : 'Offer'}</p>
          <p className="text-sm font-bold text-amber-300">{showAmount} XRP</p>
        </div>
        <div>
          <p className="text-[9px] text-slate-500">BIN</p>
          <p className="text-sm font-bold text-slate-300">{offer.bin_price}</p>
        </div>
        <div>
          <p className="text-[9px] text-slate-500">{(isPending || isCountered) ? 'Window' : 'Closed'}</p>
          <p className="text-[11px] font-semibold text-slate-300">{(isPending || isCountered) ? fmtTimeLeft(offer.expires_at) : '—'}</p>
        </div>
      </div>

      {offer.message && (
        <p className="text-[11px] text-slate-400 italic bg-slate-900/40 rounded px-2 py-1">"{offer.message}"</p>
      )}

      {/* HOST actions on PENDING */}
      {role === 'inbox' && isPending && (
        <div className="flex gap-2">
          <Button
            data-testid={`accept-${offer.offer_id.slice(0,8)}`}
            disabled={!!acting}
            onClick={() => act('accept')}
            size="sm"
            className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 h-8 text-xs"
          >
            {acting === 'accept' ? <Loader2 className="w-3 h-3 animate-spin" /> : (<><Check className="w-3 h-3 mr-1" />Accept</>)}
          </Button>
          <Button
            data-testid={`counter-${offer.offer_id.slice(0,8)}`}
            disabled={!!acting}
            onClick={() => { setCounter(((+offer.offer_amount + +offer.bin_price) / 2).toFixed(2)); setShowCounter(true); }}
            size="sm"
            variant="outline"
            className="flex-1 border-orange-500/40 text-orange-300 h-8 text-xs"
          >
            <Repeat className="w-3 h-3 mr-1" />Counter
          </Button>
          <Button
            data-testid={`reject-${offer.offer_id.slice(0,8)}`}
            disabled={!!acting}
            onClick={() => act('reject')}
            size="sm"
            variant="ghost"
            className="text-red-400 h-8 text-xs px-2"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* BUYER actions on COUNTERED */}
      {role === 'outbox' && isCountered && (
        <div className="flex gap-2">
          <Button
            data-testid={`buyer-accept-${offer.offer_id.slice(0,8)}`}
            disabled={!!acting}
            onClick={() => act('accept')}
            size="sm"
            className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 h-8 text-xs"
          >
            {acting === 'accept' ? <Loader2 className="w-3 h-3 animate-spin" /> : `Accept ${offer.counter_amount} XRP`}
          </Button>
          <Button
            data-testid={`buyer-withdraw-${offer.offer_id.slice(0,8)}`}
            disabled={!!acting}
            onClick={() => act('withdraw')}
            size="sm"
            variant="ghost"
            className="text-red-400 h-8 text-xs"
          >
            Decline
          </Button>
        </div>
      )}

      {/* BUYER withdraw on PENDING */}
      {role === 'outbox' && isPending && (
        <Button
          data-testid={`buyer-withdraw-pending-${offer.offer_id.slice(0,8)}`}
          disabled={!!acting}
          onClick={() => act('withdraw')}
          size="sm"
          variant="outline"
          className="w-full border-red-500/40 text-red-300 h-7 text-[11px]"
        >
          Withdraw Offer
        </Button>
      )}

      <Dialog open={showCounter} onOpenChange={setShowCounter}>
        <DialogContent data-testid="counter-dialog" className="bg-slate-900 border-slate-700 max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-white">Counter the Offer</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Must be above {offer.offer_amount} and below {offer.bin_price} XRP.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="counter-input"
            type="number"
            step="0.01"
            min={counterMin}
            max={counterMax}
            value={counter}
            onChange={(e) => setCounter(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white"
          />
          <Button
            data-testid="submit-counter"
            onClick={submitCounter}
            disabled={acting === 'counter'}
            className="w-full bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold"
          >
            {acting === 'counter' ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send Counter · ${counter} XRP`}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function OffersSection() {
  const [inbox, setInbox] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [tab, setTab] = useState('inbox');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [i, o] = await Promise.all([
        axios.get(`${API}/offers/inbox`),
        axios.get(`${API}/offers/outbox`),
      ]);
      setInbox(i.data?.offers || []);
      setOutbox(o.data?.offers || []);
    } catch (e) {
      // silent — section just stays empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const inboxActive = inbox.filter(o => ['pending', 'countered'].includes(o.status));
  const outboxActive = outbox.filter(o => ['pending', 'countered'].includes(o.status));

  // Hide entire section when nothing to show (active + recent history)
  if (!loading && inbox.length === 0 && outbox.length === 0) return null;

  const visible = tab === 'inbox' ? inbox : outbox;

  return (
    <section className="px-4 pt-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Handshake className="w-4 h-4 text-amber-400" />
          <h3 data-testid="offers-heading" className="text-sm font-bold text-white">Offers</h3>
        </div>
        <div className="flex bg-slate-800/60 border border-slate-700 rounded-lg p-0.5 text-[11px]">
          <button
            data-testid="offers-tab-inbox"
            onClick={() => setTab('inbox')}
            className={`px-3 py-1 rounded-md transition ${tab === 'inbox' ? 'bg-amber-500/20 text-amber-300 font-semibold' : 'text-slate-400'}`}
          >
            Inbox {inboxActive.length > 0 && <span className="ml-1 text-[9px] text-amber-400">·{inboxActive.length}</span>}
          </button>
          <button
            data-testid="offers-tab-outbox"
            onClick={() => setTab('outbox')}
            className={`px-3 py-1 rounded-md transition ${tab === 'outbox' ? 'bg-amber-500/20 text-amber-300 font-semibold' : 'text-slate-400'}`}
          >
            Sent {outboxActive.length > 0 && <span className="ml-1 text-[9px] text-amber-400">·{outboxActive.length}</span>}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-slate-500 text-center py-3">Loading offers…</p>
      ) : visible.length === 0 ? (
        <p data-testid="offers-empty" className="text-xs text-slate-500 text-center py-3">
          {tab === 'inbox' ? 'No offers received yet.' : "You haven't made any offers."}
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map(o => (
            <OfferRow key={o.offer_id} offer={o} role={tab} onUpdated={load} />
          ))}
        </div>
      )}
    </section>
  );
}
